import React, { memo, useEffect, useMemo, useRef, useState } from 'react'
import PhoneInput, { getCountryCallingCode } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import './PhoneNumberField.css'

// Searchable country dropdown (by country name), passed to react-phone-number-input
// via its `countrySelectComponent` prop. Built with plain React only - no extra deps.
const PhoneCountrySelect = ({ value, onChange, options, disabled, readOnly, iconComponent: Icon }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const listRef = useRef(null);
    const listboxId = React.useId();

    // Resolving calling codes for every option is relatively expensive, so only
    // recompute when the incoming `options` list actually changes.
    const countryOptions = useMemo(
        () =>
            (options || [])
                .filter((option) => option.value)
                .map((option) => {
                    let callingCode = '';
                    try {
                        callingCode = getCountryCallingCode(option.value);
                    } catch (e) {
                        callingCode = '';
                    }
                    return { ...option, callingCode };
                }),
        [options]
    );
    const selected = countryOptions.find((option) => option.value === value);
    const search = query.trim().toLowerCase().replace(/^\+/, '');
    const filtered = useMemo(
        () =>
            countryOptions.filter((option) =>
                (option.label || '').toLowerCase().includes(search) ||
                (option.callingCode || '').includes(search)
            ),
        [countryOptions, search]
    );

    // Keep the highlighted item in range as the filtered list changes.
    useEffect(() => {
        setActiveIndex(0);
    }, [search, open]);

    // Scroll the highlighted item into view during keyboard navigation.
    useEffect(() => {
        if (!open || !listRef.current) return;
        const activeEl = listRef.current.children[activeIndex];
        if (activeEl) activeEl.scrollIntoView({ block: 'nearest' });
    }, [activeIndex, open]);

    const handleSelect = (val) => {
        onChange(val || undefined);
        setOpen(false);
        setQuery('');
    };

    // Closes the dropdown only when focus leaves the whole component.
    const handleBlur = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setOpen(false);
            setQuery('');
        }
    };

    const handleKeyDown = (e) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex((prev) => Math.min(prev + 1, filtered.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex((prev) => Math.max(prev - 1, 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (filtered[activeIndex]) {
                    handleSelect(filtered[activeIndex].value);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setOpen(false);
                setQuery('');
                break;
            default:
                break;
        }
    };

    const activeOptionId =
        open && filtered[activeIndex] ? `${listboxId}-opt-${filtered[activeIndex].value}` : undefined;

    return (
        <div className="fs-phone-country" onBlur={handleBlur}>
            <button
                type="button"
                className="fs-phone-country__trigger"
                disabled={disabled || readOnly}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={`Select country${selected?.label ? `, ${selected.label} selected` : ''}`}
                onClick={() => setOpen((prev) => !prev)}
            >
                {Icon && <Icon country={value} label={selected?.label} />}
                <span className="fs-phone-country__arrow" />
            </button>
            {open && (
                <div className="fs-phone-country__dropdown">
                    <input
                        type="text"
                        autoFocus
                        className="fs-phone-country__search"
                        placeholder="Search country"
                        role="combobox"
                        aria-controls={listboxId}
                        aria-expanded={open}
                        aria-activedescendant={activeOptionId}
                        aria-autocomplete="list"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <ul ref={listRef} className="fs-phone-country__list" role="listbox" id={listboxId} aria-label="Countries">
                        {filtered.length === 0 ? (
                            <li className="fs-phone-country__empty">No country found</li>
                        ) : (
                            filtered.map((option, index) => (
                                <li
                                    key={option.value}
                                    id={`${listboxId}-opt-${option.value}`}
                                    role="option"
                                    aria-selected={option.value === value}
                                    className={`fs-phone-country__item ${option.value === value ? 'is-selected' : ''} ${index === activeIndex ? 'is-active' : ''}`}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleSelect(option.value);
                                    }}
                                >
                                    {Icon && <Icon country={option.value} label={option.label} />}
                                    <span className="fs-phone-country__name">{option.label}</span>
                                    <span className="fs-phone-country__code">+{option.callingCode}</span>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

/**
 * Formik-friendly phone field built on react-phone-number-input.
 * Holds the value in E.164 format (e.g. "+918287259604") so the country
 * is auto-selected from the number itself.
 *
 * Props:
 *   - disabled    : disables the whole field (country picker + number input).
 *   - lockCountry : disables ONLY the country dropdown (number stays editable),
 *                   so the country is display-only.
 *
 * Usage: <Field name="contact" component={PhoneNumberField} placeholder="..." disabled={false} lockCountry />
 */
const PhoneNumberField = memo(({ field, form, placeholder, disabled, lockCountry }) => {
    const hasError = form.errors?.[field.name] && form.touched?.[field.name];
    // Wrap the country select so it can be locked independently of the number input.
    // Memoised so PhoneInput doesn't remount it on every render.
    const countrySelectComponent = useMemo(
        () => (props) => <PhoneCountrySelect {...props} disabled={props.disabled || lockCountry} />,
        [lockCountry]
    );
    return (
        <PhoneInput
            international
            defaultCountry="IN"
            countryCallingCodeEditable={false}
            countrySelectComponent={countrySelectComponent}
            id={field.name}
            name={field.name}
            placeholder={placeholder}
            disabled={disabled}
            value={field.value || undefined}
            onChange={(value) => form.setFieldValue(field.name, value || '')}
            onBlur={() => form.setFieldTouched(field.name, true)}
            className={`fs-phone-input mt-2 ${hasError ? 'fs-phone-input--error' : ''} ${disabled ? 'fs-phone-input--disabled' : ''}`}
        />
    );
})

export default PhoneNumberField
